import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { callConfig } from '../configs/call';
import { getSignalingToken, getTurnConfig } from '../services/call';
import { createCallController } from './createCallController';
import { mapControllerStateToCallUi } from './stateMappers';
import { CONTROLLER_COMMAND_TYPE as CCT } from '../call-prototype/domain/controllerCommands.js';
import { makeInitialCallModel } from '../call-prototype/domain/callModel.js';

const SIGNALING_AUTH_TIMEOUT_MS = 8000;
const documentScopedDeviceIds = new Map();

const makeDeviceId = (prefix = 'web') =>
    `${prefix}-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;

const getNavigationType = () => {
    try {
        return window.performance?.getEntriesByType?.('navigation')?.[0]?.type || 'navigate';
    } catch {
        return 'navigate';
    }
};

const getTabDeviceId = (storageKey) => {
    const tabStorageKey = `${storageKey}:tab`;
    const cachedTabId = documentScopedDeviceIds.get(tabStorageKey);
    if (cachedTabId) {
        return cachedTabId;
    }

    let storedTabId = null;
    try {
        storedTabId = sessionStorage.getItem(tabStorageKey);
    } catch {
        // Fall back to a document-scoped id when sessionStorage is unavailable.
    }

    const navigationType = getNavigationType();
    const canReuseStoredTab =
        Boolean(storedTabId) &&
        (navigationType === 'reload' || navigationType === 'back_forward');
    const tabId = canReuseStoredTab ? storedTabId : makeDeviceId('tab');

    try {
        sessionStorage.setItem(tabStorageKey, tabId);
    } catch {
        // The in-memory cache still keeps this tab stable until the document unloads.
    }

    documentScopedDeviceIds.set(tabStorageKey, tabId);
    return tabId;
};

const getStableDeviceId = (inputDeviceId) => {
    if (inputDeviceId) {
        return inputDeviceId;
    }

    try {
        const storageKey = callConfig.deviceIdStorageKey || 'call-device-id';
        const existing = localStorage.getItem(storageKey);
        const browserDeviceId = existing || makeDeviceId('browser');
        if (!existing) {
            localStorage.setItem(storageKey, browserDeviceId);
        }

        return `${browserDeviceId}:${getTabDeviceId(storageKey)}`;
    } catch {
        return `${makeDeviceId('browser')}:${getTabDeviceId('call-device-id')}`;
    }
};

const readJsonFromStorage = (key) => {
    if (!key) return null;

    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch {
        return null;
    }
};

const writeJsonToStorage = (key, value) => {
    if (!key) return;
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch {
        // best effort
    }
};

const removeFromStorage = (key) => {
    if (!key) return;
    try {
        localStorage.removeItem(key);
    } catch {
        // best effort
    }
};

export const useCallSession = ({
    enabled = true,
    userRoleUuid,
    userId,
    deviceId: inputDeviceId
} = {}) => {
    const instanceRef = useRef(null);
    const [state, setState] = useState(() => makeInitialCallModel());
    const stateRef = useRef(state);
    const connectionAttemptRef = useRef(null);
    const [wsMessageLog, setWsMessageLog] = useState([]);
    const [runtimeError, setRuntimeError] = useState(null);

    const deviceIdRef = useRef(getStableDeviceId(inputDeviceId));
    const persistedResumeRef = useRef(null);
    const resumeEnabled = callConfig.resumeEnabled;
    const resumeStorageKey = useMemo(
        () => `call-resume:${userId || 'anonymous'}:${deviceIdRef.current}`,
        [userId]
    );

    useEffect(() => {
        if (!enabled || !callConfig.signalingWsUrl) {
            return undefined;
        }

        if (resumeEnabled) {
            persistedResumeRef.current = readJsonFromStorage(resumeStorageKey);
        } else {
            persistedResumeRef.current = null;
            removeFromStorage(resumeStorageKey);
        }

        const instance = createCallController({
            signalingUrl: callConfig.signalingWsUrl,
            allowServerCallStateSnapshots: resumeEnabled,
            rtcConfigProvider: async () => {
                const result = await getTurnConfig({
                    userRoleUuid,
                    userId
                });
                return {
                    iceServers: result.iceServers,
                    iceTransportPolicy: callConfig.iceTransportPolicy
                };
            }
        });

        if (resumeEnabled) {
            instance.controller.hydrateResumeSnapshot?.(persistedResumeRef.current);
        }

        instanceRef.current = instance;
        const initialState = instance.controller.getState();
        stateRef.current = initialState;
        setState(initialState);
        setWsMessageLog(instance.controller.getWsMessageLog?.() || []);

        const persistResumeSnapshot = () => {
            if (resumeEnabled) {
                const snapshot = instance.controller.getResumeSnapshot?.();
                if (snapshot?.sessionId) {
                    writeJsonToStorage(resumeStorageKey, {
                        sessionId: snapshot.sessionId,
                        lastServerSeqSeen: snapshot.lastServerSeqSeen ?? 0,
                        updatedAt: Date.now()
                    });
                    persistedResumeRef.current = {
                        sessionId: snapshot.sessionId,
                        lastServerSeqSeen: snapshot.lastServerSeqSeen ?? 0
                    };
                    return;
                }
            }

            removeFromStorage(resumeStorageKey);
            persistedResumeRef.current = null;
        };

        const unsubscribe = instance.controller.subscribe((nextState) => {
            stateRef.current = nextState;
            setState(nextState);
            persistResumeSnapshot();
        });
        const unsubscribeWsLog = instance.controller.subscribeWsMessageLog((snapshot) => {
            setWsMessageLog(snapshot || []);
            persistResumeSnapshot();
        });

        return () => {
            unsubscribe();
            unsubscribeWsLog();
            void instance.controller.dispose();
            instanceRef.current = null;
            connectionAttemptRef.current = null;
            setWsMessageLog([]);
        };
    }, [enabled, resumeEnabled, resumeStorageKey, userId, userRoleUuid]);

    const dispatchCommand = useCallback(async (command) => {
        if (!instanceRef.current?.controller) {
            return { ok: false, error: 'Call controller is not initialized.' };
        }

        const result = await instanceRef.current.controller.dispatch(command);
        if (!result.ok) {
            setRuntimeError(result.error);
        }
        return result;
    }, []);

    const waitForSignalingAuthentication = useCallback((controller) => {
        return new Promise((resolve) => {
            let settled = false;
            let timer = null;
            let unsubscribe = () => {};

            const finish = (result) => {
                if (settled) return;
                settled = true;
                if (timer) clearTimeout(timer);
                unsubscribe();
                resolve(result);
            };

            const inspectState = (nextState) => {
                const signalingState = nextState?.signalingConnectionState;
                if (signalingState === 'AUTHENTICATED' || signalingState === 'READY') {
                    finish({ ok: true });
                    return;
                }

                if (signalingState === 'DISCONNECTED' || signalingState === 'ERROR') {
                    finish({
                        ok: false,
                        error: nextState?.error?.message || 'A ligação ao serviço de chamadas foi interrompida.'
                    });
                }
            };

            unsubscribe = controller.subscribe(inspectState);
            timer = setTimeout(() => {
                finish({
                    ok: false,
                    error: 'O serviço de chamadas não confirmou a ligação dentro do tempo esperado.'
                });
            }, SIGNALING_AUTH_TIMEOUT_MS);
            inspectState(controller.getState());
        });
    }, []);

    const connectSignaling = useCallback(async ({ tryResume = true, replaceExisting = false } = {}) => {
        if (connectionAttemptRef.current) {
            return await connectionAttemptRef.current;
        }

        const attempt = (async () => {
            const instance = instanceRef.current;
            const controller = instance?.controller;
            if (!controller) {
                return { ok: false, error: 'Call controller is not initialized.' };
            }

            const signalingState = stateRef.current?.signalingConnectionState;
            const transportIsOpen = instance.adapters?.signalingAdapter?.isConnected?.() === true;

            if (
                !replaceExisting &&
                transportIsOpen &&
                (signalingState === 'AUTHENTICATED' || signalingState === 'READY')
            ) {
                return { ok: true };
            }

            if (
                !replaceExisting &&
                transportIsOpen &&
                (signalingState === 'CONNECTING' || signalingState === 'CONNECTED')
            ) {
                return await waitForSignalingAuthentication(controller);
            }

            try {
                setRuntimeError(null);

                if (replaceExisting || transportIsOpen) {
                    await controller.dispatch({ type: CCT.DISCONNECT_SIGNALING });
                }

                const { token } = await getSignalingToken({
                    userRoleUuid,
                    userId
                });

                const resumeSnapshot =
                    resumeEnabled && tryResume && persistedResumeRef.current?.sessionId
                        ? persistedResumeRef.current
                        : null;

                const connectResult = await controller.dispatch({
                    type: CCT.CONNECT_SIGNALING,
                    token,
                    deviceId: deviceIdRef.current,
                    ...(resumeSnapshot
                        ? {
                              resume: {
                                  sessionId: resumeSnapshot.sessionId,
                                  lastServerSeqSeen: Number(resumeSnapshot.lastServerSeqSeen ?? 0) || 0
                              }
                          }
                        : {})
                });

                if (!connectResult.ok) {
                    setRuntimeError(connectResult.error);
                    return connectResult;
                }

                const authenticationResult = await waitForSignalingAuthentication(controller);
                if (!authenticationResult.ok) {
                    await controller.dispatch({ type: CCT.DISCONNECT_SIGNALING });
                    setRuntimeError(authenticationResult.error);
                }
                return authenticationResult;
            } catch (error) {
                const message = error instanceof Error ? error.message : String(error);
                setRuntimeError(message);
                return {
                    ok: false,
                    error: message
                };
            }
        })();

        connectionAttemptRef.current = attempt;
        try {
            return await attempt;
        } finally {
            if (connectionAttemptRef.current === attempt) {
                connectionAttemptRef.current = null;
            }
        }
    }, [resumeEnabled, userId, userRoleUuid, waitForSignalingAuthentication]);

    const reconnectSignaling = useCallback(
        async ({ tryResume = false } = {}) => {
            return await connectSignaling({
                tryResume,
                replaceExisting: true
            });
        },
        [connectSignaling]
    );

    useEffect(() => {
        if (
            state?.signalingConnectionState === 'AUTHENTICATED' ||
            state?.signalingConnectionState === 'READY'
        ) {
            setRuntimeError(null);
        }
    }, [state?.signalingConnectionState]);

    const disconnectSignaling = useCallback(async () => {
        return await dispatchCommand({ type: CCT.DISCONNECT_SIGNALING });
    }, [dispatchCommand]);

    const acquireLocalMedia = useCallback(
        async ({ audio = true, video = true } = {}) => {
            return await dispatchCommand({
                type: CCT.ACQUIRE_LOCAL_MEDIA,
                audio,
                video
            });
        },
        [dispatchCommand]
    );

    const createCall = useCallback(
        async ({ calleeUserId, audio = true, video = true }) => {
            return await dispatchCommand({
                type: CCT.CREATE_CALL,
                calleeUserId,
                media: { audio, video }
            });
        },
        [dispatchCommand]
    );

    const acceptCall = useCallback(async () => {
        if (!state.activeCall?.callId) {
            return { ok: false, error: 'No active call to accept.' };
        }

        return await dispatchCommand({
            type: CCT.ACCEPT_CALL,
            callId: state.activeCall.callId
        });
    }, [dispatchCommand, state.activeCall?.callId]);

    const rejectCall = useCallback(async () => {
        if (!state.activeCall?.callId) {
            return { ok: false, error: 'No active call to reject.' };
        }

        return await dispatchCommand({
            type: CCT.REJECT_CALL,
            callId: state.activeCall.callId
        });
    }, [dispatchCommand, state.activeCall?.callId]);

    const endCall = useCallback(async () => {
        if (!state.activeCall?.callId) {
            return { ok: false, error: 'No active call to end.' };
        }

        const localUserId = state.session?.userId || null;
        const localSessionId = state.session?.sessionId || null;
        const participant =
            localUserId && state.activeCall?.participants
                ? state.activeCall.participants[localUserId]
                : null;
        const owningSessionId = participant?.owningSessionId || null;

        if (owningSessionId && localSessionId && owningSessionId !== localSessionId) {
            return {
                ok: false,
                error: 'This session is not the owner of the call. End the call from the owner device/session.'
            };
        }

        return await dispatchCommand({
            type: CCT.END_CALL,
            callId: state.activeCall.callId,
            reason: 'ended-by-user'
        });
    }, [dispatchCommand, state.activeCall?.callId]);

    const toggleLocalAudio = useCallback(async () => {
        const enabled = !state.media.audio.enabled;
        return await dispatchCommand({
            type: CCT.TOGGLE_LOCAL_AUDIO,
            enabled
        });
    }, [dispatchCommand, state.media.audio.enabled]);

    const toggleLocalVideo = useCallback(async () => {
        const enabled = !state.media.video.enabled;
        return await dispatchCommand({
            type: CCT.TOGGLE_LOCAL_VIDEO,
            enabled
        });
    }, [dispatchCommand, state.media.video.enabled]);

    const refreshDevices = useCallback(async () => {
        return await dispatchCommand({ type: CCT.REFRESH_MEDIA_DEVICES });
    }, [dispatchCommand]);

    const stopLocalMedia = useCallback(async () => {
        return await dispatchCommand({ type: CCT.STOP_LOCAL_MEDIA });
    }, [dispatchCommand]);

    const selectAudioInputDevice = useCallback(
        async (deviceId) => {
            return await dispatchCommand({
                type: CCT.SELECT_AUDIO_INPUT_DEVICE,
                deviceId
            });
        },
        [dispatchCommand]
    );

    const selectVideoInputDevice = useCallback(
        async (deviceId) => {
            return await dispatchCommand({
                type: CCT.SELECT_VIDEO_INPUT_DEVICE,
                deviceId
            });
        },
        [dispatchCommand]
    );

    const uiState = useMemo(() => mapControllerStateToCallUi(state), [state]);

    const localStream = instanceRef.current?.adapters?.mediaAdapter?.getLocalStream() || null;
    const remoteStream =
        instanceRef.current?.adapters?.peerConnectionAdapter?.getRemoteStream() || null;

    const errorMessage = runtimeError || uiState.errorMessage || null;

    const actions = useMemo(
        () => ({
            connectSignaling,
            reconnectSignaling,
            disconnectSignaling,
            acquireLocalMedia,
            createCall,
            acceptCall,
            rejectCall,
            endCall,
            stopLocalMedia,
            toggleLocalAudio,
            toggleLocalVideo,
            refreshDevices,
            selectAudioInputDevice,
            selectVideoInputDevice
        }),
        [
            connectSignaling,
            reconnectSignaling,
            disconnectSignaling,
            acquireLocalMedia,
            createCall,
            acceptCall,
            rejectCall,
            endCall,
            stopLocalMedia,
            toggleLocalAudio,
            toggleLocalVideo,
            refreshDevices,
            selectAudioInputDevice,
            selectVideoInputDevice
        ]
    );

    return {
        state,
        wsMessageLog,
        uiState,
        localStream,
        remoteStream,
        errorMessage,
        actions
    };
};
